/*
 * concierge-capture.js — popup + sheet timing and suppression layer.
 *
 * Strategy
 * - Desktop popup: show after 25s OR 50% scroll, whichever comes first.
 * - Mobile sheet:  show after 35s OR 60% scroll, whichever comes first.
 * - 30-day suppression cookie on close OR submit.
 * - No display on /cart or /checkout pages (we test path on load).
 * - No display if cookie banner / cart drawer is open (modal stacking guard).
 *
 * Tracking: window.dispatchEvent(new CustomEvent("prarvi:" + name, { detail })).
 * Hook your analytics into prarvi:popup_view, popup_close, popup_submit,
 * popup_retail_help_select, popup_salon_pricing_select, popup_phone_click,
 * inline_help_submit.
 *
 * Closes P2-013 timing + tracking. Form-side covered by P2-010 / P2-011 /
 * P2-012 (snippets/concierge-capture.liquid).
 */
(function () {
  "use strict";

  var COOKIE_NAME = "prarvi_concierge_seen";
  var COOKIE_DAYS = 30;
  var DESKTOP_DELAY_MS = 25000;
  var DESKTOP_SCROLL_RATIO = 0.5;
  var MOBILE_DELAY_MS = 35000;
  var MOBILE_SCROLL_RATIO = 0.6;
  var MOBILE_BREAKPOINT = 768;

  function fire(name, detail) {
    if (window.Prarvi && typeof window.Prarvi.track === "function") {
      window.Prarvi.track(name, detail);
      return;
    }
    try { window.dispatchEvent(new CustomEvent("prarvi:" + name, { detail: detail || {} })); } catch (e) {}
  }

  function getCookie(name) {
    var parts = ("; " + document.cookie).split("; " + name + "=");
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  }

  function setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + d.toUTCString();
    }
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = name + "=" + value + expires + "; path=/; SameSite=Lax" + secure;
  }

  function path() { return window.location.pathname || ""; }

  function isSuppressedPath() {
    var p = path();
    if (p.indexOf("/cart") === 0 || p.indexOf("/checkout") === 0 || p.indexOf("/account") === 0) return true;
    if (p.indexOf("/products/") === 0 || p.indexOf("/collections/") === 0) return true;
    return [
      "/pages/wholesale-hair-extensions",
      "/pages/research-hair-testers",
      "/pages/thinning-hair-solutions",
      "/pages/hair-samples-shade-texture-match",
      "/pages/human-hair-wigs-toppers-cosmetic"
    ].indexOf(p) !== -1;
  }

  function anotherModalOpen() {
    // Cookie banner / GDPR / cart drawer / lightbox / existing newsletter.
    // #accept-cookies is the theme's GDPR banner (sections/GDPR-modal.liquid).
    // It's removed from the DOM after Accept is clicked, so a hit here means
    // it's currently visible. P2-017.
    var sels = ["#accept-cookies", ".gdpr-modal.open", ".cookie-banner:not([hidden])", ".cart-drawer.open", ".mfp-ready", "#email-modal.open"];
    for (var i = 0; i < sels.length; i++) {
      if (document.querySelector(sels[i])) return true;
    }
    return false;
  }

  function pickVariant() {
    var w = window.innerWidth || document.documentElement.clientWidth;
    return w < MOBILE_BREAKPOINT ? "sheet" : "popup";
  }

  function show(el) {
    if (!el || !el.hasAttribute("hidden")) return;
    el.removeAttribute("hidden");
    el.removeAttribute("aria-hidden");
    el.removeAttribute("inert");
    fire("popup_view", { variant: el.getAttribute("data-concierge") });
  }

  function dismissAndSuppress(el, reason) {
    if (!el) return;
    el.setAttribute("hidden", "");
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("inert", "");
    setCookie(COOKIE_NAME, "1", COOKIE_DAYS);
    fire("popup_close", { variant: el.getAttribute("data-concierge"), reason: reason });
  }

  function wireCloseButtons(root) {
    root.querySelectorAll("[data-concierge-close]").forEach(function (btn) {
      btn.addEventListener("click", function () { dismissAndSuppress(root, "close_button"); });
    });
  }

  function wireFallbackCloseButtons() {
    document.querySelectorAll("[data-concierge-close]").forEach(function (btn) {
      if (btn.getAttribute("data-concierge-close-wired") === "true") return;
      btn.setAttribute("data-concierge-close-wired", "true");
      btn.addEventListener("click", function () {
        var root = btn.closest(".concierge");
        dismissAndSuppress(root, "close_button");
      });
    });
  }

  var SALON_TAGS = ", salon_pricing_interest, b2b_lead, salon_stylist, wholesale_request";

  function currentIntent(root) {
    var intentEl = root.querySelector("[data-concierge-lead-intent]");
    return intentEl ? intentEl.value : "retail";
  }

  function applyIntent(root, intent, silent) {
    var intentEl = root.querySelector("[data-concierge-lead-intent]");
    if (intentEl) intentEl.value = intent;

    // Salon/wholesale is the only intent that appends B2B routing tags.
    var tagsEl = root.querySelector('input[name="contact[tags]"]');
    if (tagsEl) {
      if (intent === "salon") {
        if (tagsEl.value.indexOf("salon_stylist") === -1) tagsEl.value = tagsEl.value + SALON_TAGS;
      } else if (tagsEl.value.indexOf("salon_stylist") !== -1) {
        tagsEl.value = tagsEl.value.replace(SALON_TAGS, "");
      }
    }

    // Show only the route link(s) matching the selected intent.
    var routesNav = root.querySelector("[data-concierge-routes]");
    if (routesNav) {
      var links = routesNav.querySelectorAll("[data-concierge-route-link]");
      var anyShown = false;
      for (var i = 0; i < links.length; i++) {
        var match = links[i].getAttribute("data-concierge-route") === intent;
        links[i].style.display = match ? "inline-block" : "none";
        if (match) anyShown = true;
      }
      routesNav.style.display = anyShown ? "block" : "none";
    }

    // Preserve legacy tracking events for the two original intents (skip on
    // the initial programmatic sync so we don't emit on page load).
    if (silent) return;
    if (intent === "retail") fire("popup_retail_help_select", { audience: "retail", intent: "retail" });
    else if (intent === "salon") fire("popup_salon_pricing_select", { audience: "salon", intent: "salon" });
  }

  function wireHelpType(root) {
    var sel = root.querySelector("[data-concierge-intent-select]");
    if (sel) {
      var syncFromSelect = function (silent) {
        var opt = sel.options[sel.selectedIndex];
        var intent = (opt && opt.getAttribute("data-intent")) || "retail";
        applyIntent(root, intent, silent === true);
      };
      sel.addEventListener("change", function () { syncFromSelect(false); });
      syncFromSelect(true);
    }

    // Backward-compatible: radios using the original data-concierge-help-type API.
    root.querySelectorAll("[data-concierge-help-type]").forEach(function (el) {
      el.addEventListener("change", function () {
        applyIntent(root, el.getAttribute("data-concierge-help-type"));
      });
    });
  }

  function wireRoutes(root) {
    root.querySelectorAll("[data-concierge-route-link]").forEach(function (a) {
      a.addEventListener("click", function () {
        fire("concierge_route_click", { lead_intent: currentIntent(root), href: a.getAttribute("href") });
      });
    });
  }

  function stage2Filled(root) {
    var fields = root.querySelectorAll("[data-concierge-stage2]");
    for (var i = 0; i < fields.length; i++) {
      if ((fields[i].value || "").trim() !== "") return true;
    }
    return false;
  }

  function wirePhone(root) {
    root.querySelectorAll("[data-concierge-phone]").forEach(function (a) {
      a.addEventListener("click", function () { fire("popup_phone_click"); });
    });
  }

  function wireSubmit(root) {
    var form = root.querySelector("form");
    if (!form) return;
    form.addEventListener("submit", function () {
      var isInline = root.getAttribute("data-concierge") === "inline";
      var variant = root.getAttribute("data-concierge");
      var intent = currentIntent(root);
      var hasStage2 = stage2Filled(root);
      var leadStage = hasStage2 ? "short_request" : "email_only";

      var stageEl = root.querySelector("[data-concierge-lead-stage]");
      if (stageEl) stageEl.value = leadStage;

      var tagsEl = root.querySelector('input[name="contact[tags]"]');
      if (tagsEl && tagsEl.value.indexOf(leadStage) === -1) {
        tagsEl.value = tagsEl.value + ", " + leadStage + ", intent_" + intent;
      }

      // Legacy variant-level event preserved.
      fire(isInline ? "inline_help_submit" : "popup_submit", {
        audience: intent === "salon" ? "salon" : "retail",
        intent: intent,
        variant: variant
      });

      // P1-328 progressive lead-capture events.
      fire("lead_email_capture", { lead_intent: intent, lead_stage: leadStage, variant: variant });
      if (hasStage2) {
        fire("lead_short_request_submit", { lead_intent: intent, lead_stage: leadStage, variant: variant });
      }

      if (!isInline) setCookie(COOKIE_NAME, "1", COOKIE_DAYS);
    });
  }

  function init() {
    wireFallbackCloseButtons();

    // Inline variants: just wire tracking, no timing.
    document.querySelectorAll('.concierge--inline').forEach(function (root) {
      wireHelpType(root);
      wireRoutes(root);
      wirePhone(root);
      wireSubmit(root);
    });

    // Modal variants: timing + suppression.
    // P1-332: the popup (desktop) and sheet (mobile) markup are both rendered
    // server-side as inert/aria-hidden duplicates of the same form. Pick the one
    // that matches the current viewport and remove the other from the DOM so
    // assistive tech and crawlers only ever see a single concierge form
    // instance. Both start inert, so even before this runs nothing is
    // focusable/submittable, but removing the unused node makes the dedupe
    // definitive rather than relying on the inert attribute alone.
    var variant = pickVariant();
    var unusedVariant = variant === "sheet" ? "popup" : "sheet";
    var unusedEl = document.querySelector('.concierge[data-concierge="' + unusedVariant + '"]');
    if (unusedEl && unusedEl.parentNode) {
      unusedEl.parentNode.removeChild(unusedEl);
    }

    if (isSuppressedPath() || getCookie(COOKIE_NAME)) return;

    var el = document.querySelector('.concierge[data-concierge="' + variant + '"]');
    if (!el) return;

    wireCloseButtons(el);
    wireHelpType(el);
    wireRoutes(el);
    wirePhone(el);
    wireSubmit(el);

    var delayMs = variant === "sheet" ? MOBILE_DELAY_MS : DESKTOP_DELAY_MS;
    var scrollRatio = variant === "sheet" ? MOBILE_SCROLL_RATIO : DESKTOP_SCROLL_RATIO;
    var fired = false;

    function maybeShow(reason) {
      if (fired) return;
      if (anotherModalOpen()) return;
      fired = true;
      fire("popup_trigger", { variant: variant, reason: reason });
      show(el);
    }

    var timeoutId = window.setTimeout(function () { maybeShow("timer"); }, delayMs);

    function onScroll() {
      var docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      var ratio = (window.scrollY || window.pageYOffset) / docH;
      if (ratio >= scrollRatio) {
        window.clearTimeout(timeoutId);
        maybeShow("scroll");
        window.removeEventListener("scroll", onScroll);
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
